import {
  type BillingInterval,
  PLAN_DISPLAY,
  type SelfServePlanKey,
} from "@giftforward/entitlements";
import { AuthFloatingField } from "@/components/auth";
import { PLAN_DRAWER_STRINGS, PLAN_INTERVAL_STRINGS } from "@/constants/Plans";
import { usePlans } from "@/hooks/usePlans";
import { formatPlanCharge, selectedOffer } from "@/utils/plan-price-helper";

interface OnboardingTeamStepProps {
  /** The plan just paid for. */
  plan: SelfServePlanKey;
  /** The interval it was bought at. */
  interval: BillingInterval;
  invitedEmails: string[];
  onEmailChange: (index: number, value: string) => void;
  onContinue: () => void;
  onSkip: () => void;
  isSubmitting: boolean;
  error: string | null;
  remainingSeats?: number;
  /**
   * The charge has gone through. This step is reached only that way now, so the heading's
   * "Welcome to Good Pro!" is finally true — but the payment deserves to be named rather than
   * implied by a change of screen.
   */
  subscriptionConfirmed?: boolean;
}

export function OnboardingTeamStep({
  plan,
  interval,
  invitedEmails,
  onEmailChange,
  onContinue,
  onSkip,
  isSubmitting,
  error,
  remainingSeats,
  subscriptionConfirmed = false,
}: OnboardingTeamStepProps) {
  const { byKey } = usePlans();
  const offer = selectedOffer(byKey(plan)?.offers ?? [], interval);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onContinue();
  };

  return (
    <div className="gfg-ob-panel-in">
      <h1 className="gfg-auth-title">Welcome to {PLAN_DISPLAY[plan].name}!</h1>
      {subscriptionConfirmed && (
        <p className="gfg-auth-sub">
          {offer &&
            `${formatPlanCharge(offer)} charged. ${PLAN_INTERVAL_STRINGS.renews[offer.interval]} `}
          {PLAN_DRAWER_STRINGS.receiptNote}
        </p>
      )}
      <p className="gfg-auth-sub">
        {plan === "pro_plus"
          ? "Pro Plus has no seat limit. Invite your teammates today?"
          : `You have ${remainingSeats ?? invitedEmails.length} more seats to fill. Invite them today?`}
      </p>

      <form onSubmit={handleSubmit}>
        {/* Email validation is intentionally handled by the parent component or backend. */}
        {invitedEmails.map((email, index) => (
          <AuthFloatingField
            // biome-ignore lint/suspicious/noArrayIndexKey: Order is fixed for these specific fields
            key={index}
            label="Teammate email"
            type="email"
            value={email}
            onChange={(e) => onEmailChange(index, e.target.value)}
          />
        ))}

        {error && (
          <p className="gfg-auth-hint" style={{ color: "var(--color-good-wine)", marginTop: 8 }}>
            {error}
          </p>
        )}

        <button type="submit" className="gfg-auth-btn" disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : "Continue"}
        </button>
      </form>

      <div className="gfg-auth-alt">
        <button type="button" onClick={onSkip} disabled={isSubmitting}>
          Skip for now
        </button>
      </div>
    </div>
  );
}
